import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { FixedSizeList } from "react-window";
import { RendererProps } from "../FilePreviewModal";
import VirtualizedTable from "./VirtualizedTable";
import SearchBar from "../SearchBar";
import { escapeRegex } from "../searchUtils";

interface MatchPosition {
  row: number;
  col: number;
}

const CsvRenderer: React.FC<RendererProps> = ({ blob }) => {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef<FixedSizeList | null>(null);

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    const run = async () => {
      try {
        const text = await fileBlob.text();
        if (cancelled) return;
        const Papa = (await import("papaparse")).default;
        if (cancelled) return;
        const result = Papa.parse<string[]>(text, {
          skipEmptyLines: false,
        });
        const all = (result.data as string[][]).filter(Array.isArray);
        setRows(all);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to parse CSV");
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  const maxCols = useMemo(() => {
    if (!rows) return 0;
    return rows.reduce((m, r) => Math.max(m, r.length), 0);
  }, [rows]);

  const matchPositions = useMemo<MatchPosition[]>(() => {
    if (!search || !rows) return [];
    const re = new RegExp(escapeRegex(search), "gi");
    const positions: MatchPosition[] = [];
    rows.slice(1).forEach((row, r) => {
      row.forEach((cell, c) => {
        if (cell == null) return;
        re.lastIndex = 0;
        if (re.test(String(cell))) {
          positions.push({ row: r, col: c });
        }
      });
    });
    return positions;
  }, [search, rows]);

  useEffect(() => {
    setActiveIdx(0);
  }, [matchPositions]);

  const activeCell = matchPositions[activeIdx] ?? null;

  useEffect(() => {
    if (activeCell && listRef.current) {
      listRef.current.scrollToItem(activeCell.row, "center");
    }
  }, [activeCell]);

  const gotoNext = useCallback(() => {
    if (matchPositions.length === 0) return;
    setActiveIdx((i) => (i + 1) % matchPositions.length);
  }, [matchPositions.length]);

  const gotoPrev = useCallback(() => {
    if (matchPositions.length === 0) return;
    setActiveIdx(
      (i) => (i - 1 + matchPositions.length) % matchPositions.length,
    );
  }, [matchPositions.length]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!rows) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">CSV is empty.</Alert>
      </Box>
    );
  }

  const [headerRow, ...dataRows] = rows;
  const headerCells = Array.from({ length: maxCols }).map(
    (_, i) => headerRow?.[i] ?? `Column ${i + 1}`,
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          matchCount={matchPositions.length}
          currentMatch={
            matchPositions.length > 0 ? activeIdx + 1 : 0
          }
          onPrev={gotoPrev}
          onNext={gotoNext}
        />
      </Box>
      <VirtualizedTable
        rows={dataRows}
        headerCells={headerCells}
        searchQuery={search}
        activeCell={activeCell}
        listRef={listRef}
      />
    </Box>
  );
};

export default CsvRenderer;
